/**
 * MapView — Sprint 4.1 → 4.2C.
 *
 * Sprint 4.2C — ISP GIS:
 *   - Regiões (polígonos) renderizadas + click foca/edit/del
 *   - Network Nodes (POP/OLT/CTO/Splitter/Other) com ícones próprios
 *   - Topology lines device→node
 *   - GisPanel (substitui LayersPanel)
 *   - Modais: RegionFormModal, NetworkNodeFormModal, IxcConfigModal
 *
 * Sprint 4.2D — IXC Integration (ReadOnly):
 *   - Camada "Clientes IXC" (useCustomersLayer) com 🏠 markers por status
 *   - IxcConfigModal substitui IntegrationsModal: configuração + sync UI
 *   - listCustomersForMap no refresh popula a camada
 *   - Busca GIS expandida (q inclui região + node names)
 *   - localStorage prefs (ipwd-gis-panel-prefs)
 *
 * Decisão: criação de região aproveita o useMapSelection da Sprint 4.1.2
 * (lasso). Quando o usuário clica "+ nova" no GisPanel, ativamos lasso e
 * marcamos flag pendingRegionCreate; o selection-finalize, em vez de
 * selecionar markers, abre o RegionFormModal com a geometria desenhada.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  createDeviceLink,
  deleteFleetRegion,
  deleteNetworkNode,
  enqueueRelayReboot,
  enqueueSimpleCommand,
  getFleetMapDevices,
  getFleetMapStats,
  listCustomers,
  listCustomersForMap,
  listDeviceLinks,
  listFleetRegions,
  listGroups,
  listHealth,
  listNetworkNodes,
  listTags,
  setDeviceGeo,
} from "../../lib/api.js";
import AddDeviceAtMapModal from "./AddDeviceAtMapModal.jsx";
import AlarmCenter from "./AlarmCenter.jsx";
import BulkActionBar from "./BulkActionBar.jsx";
import {
  createDeviceMarker,
  deviceStatus,
  updateDeviceMarker,
} from "./DeviceMarker.jsx";
import { createCustomerMarker } from "./CustomerMarker.jsx";
import CustomerDetailPanel from "./CustomerDetailPanel.jsx";
import { groupByCustomer } from "./customerGroups.js";
import DeviceConfigModal from "./DeviceConfigModal.jsx";
import DeviceDetailPanel from "./DeviceDetailPanel.jsx";
import DeviceTimelineModal from "./DeviceTimelineModal.jsx";
import { hasCapability, CAP } from "../../lib/rbac.js";
import { useCriticalConfirm } from "../ConfirmCriticalAction.jsx";
import IncidentsList from "./IncidentsList.jsx";
import FleetFilters from "./FleetFilters.jsx";
import GisPanel, { loadGisPrefs, saveGisPrefs } from "./GisPanel.jsx";
import MapQuickFilters, { QUICK_MATCHERS } from "./MapQuickFilters.jsx";
import IxcConfigModal from "./IxcConfigModal.jsx";
import MapStyleSwitcher from "./MapStyleSwitcher.jsx";
import MobileDeviceSheet from "./MobileDeviceSheet.jsx";
import NetworkNodeFormModal from "./NetworkNodeFormModal.jsx";
import RegionFormModal from "./RegionFormModal.jsx";
import SelectionToolbar from "./SelectionToolbar.jsx";
import { DEFAULT_STYLE, TILE_PROVIDERS } from "./tileProviders.js";
import { useAutoRefresh } from "./useAutoRefresh.js";
import { useCustomersLayer } from "./useCustomersLayer.js";
import { useDeviceStateChanges } from "./useDeviceStateChanges.js";
import { useHeatmapLayer } from "./useHeatmapLayer.js";
import { useLeaflet } from "./useLeaflet.js";
import { useLeafletPlugins } from "./useLeafletPlugins.js";
import { useMapSelection } from "./useMapSelection.js";
import {
  useNetworkNodesLayer,
  useTopologyLayer,
} from "./useNetworkLayers.js";
import { useRegionsLayer } from "./useRegionsLayer.js";
import { t } from "../../i18n/index.js";
import { getProtectionState } from "../../lib/protection.js";

const INITIAL_CENTER = [-10.6822, -39.4914];
const INITIAL_ZOOM = 13;
const REFRESH_MS = 20000;
const CLUSTER_THRESHOLD = 100;

const EMPTY_FILTERS = {
  q: "", customer_id: "", group_id: "", tags: [], status: "",
  fw_version: "", rssi_min: null, only_selected: false,
  // Sprint 5.4 — Mapa Avançado
  health_status: "", inventory_status: "", installer: "",
};

function matchesFilters(d, f, selectedIds, regionsByDevice, nodesByDevice) {
  if (f.customer_id && d.customer_id !== f.customer_id) return false;
  if (f.group_id && d.group_id !== f.group_id) return false;
  if (f.fw_version && d.fw_version !== f.fw_version) return false;
  if (f.status) {
    if (f.status === "ota_error") {
      if (d.ota_state !== "failed") return false;
    } else if (f.status === "relay_off") {
      if (!d.online || d.relay !== "off") return false;
    } else if (f.status === "weak") {
      if (!d.online || !d.weak_signal) return false;
    } else if (f.status === "online") {
      if (!d.online || d.weak_signal || d.relay === "off") return false;
    } else if (f.status === "offline") {
      if (d.online) return false;
    } else {
      const s = deviceStatus(d);
      if (s !== f.status) return false;
    }
  }
  if (f.rssi_min != null && (d.rssi == null || d.rssi < f.rssi_min)) return false;
  // Sprint 5.4 — filtros do Mapa Avançado
  if (f.health_status && d.health_status !== f.health_status) return false;
  if (f.inventory_status && d.inventory_status !== f.inventory_status) return false;
  if (f.installer && d.installer !== f.installer) return false;
  if (f.tags && f.tags.length > 0) {
    const names = new Set((d.tags || []).map((t) => t.name));
    if (!f.tags.every((t) => names.has(t))) return false;
  }
  if (f.q && f.q.trim() !== "") {
    const q = f.q.toLowerCase().trim();
    // Sprint 4.2C — busca GIS expandida
    const regionNames = (regionsByDevice?.get(d.device_id) || []).map((r) => r.name);
    const nodeNames = (nodesByDevice?.get(d.device_id) || []).flatMap((n) =>
      [n.name, n.code].filter(Boolean),
    );
    const hay = [
      d.device_id, d.customer_code, d.customer_name,
      d.group_name, d.location_name, d.address,
      ...(d.tags || []).map((t) => t.name),
      ...regionNames, ...nodeNames,
    ].filter(Boolean).map((s) => String(s).toLowerCase());
    if (!hay.some((s) => s.includes(q))) return false;
  }
  if (f.only_selected && selectedIds && !selectedIds.has(d.device_id)) return false;
  return true;
}

function deviceLayerCategory(d) {
  if (!d.online) return "offline";
  const ota = d.ota_state;
  if (ota === "pending" || ota === "delivered" ||
      ota === "downloading" || ota === "flashing") {
    return "ota";
  }
  if (d.relay === "off") return "relay_off";
  if (d.weak_signal) return "weak";
  return "online";
}

function fmtTime(d) {
  if (!d) return "—";
  return d.toLocaleTimeString("pt-BR", {
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

export default function MapView({ onNavigate }) {
  const confirmCritical = useCriticalConfirm(); // S3.10A — confirmação de ação crítica
  const { L, ready: leafletReady, error: leafletError } = useLeaflet();
  const { heatReady, clusterReady } = useLeafletPlugins(L);

  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const tileLayerRef = useRef(null);
  const clusterLayerRef = useRef(null);
  const markersByIdRef = useRef(new Map());
  const dragModeRef = useRef(new Set());
  const didFitBoundsRef = useRef(false);

  const [devices, setDevices] = useState([]);
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  // MAP-UX-1D — filtro rápido (chip-bar): null = Todos, ou uma chave de
  // QUICK_MATCHERS (online/offline/protected/fw_stale/ota_pending/hmac/clone/
  // watchdog_off/attention). Aditivo ao filtro de FleetFilters + camadas.
  const [quickFilter, setQuickFilter] = useState(null);
  const [focusDeviceId, setFocusDeviceId] = useState(null);
  // M1 — sidebar recolhível. M1.1C — inicia RECOLHIDA no mobile (< 768px).
  const [filtersOpen, setFiltersOpen] = useState(() =>
    typeof window === "undefined"
      ? true
      : !window.matchMedia("(max-width: 767px)").matches,
  );

  // P0-5 — detecção reativa de mobile (< 768px).
  const [isMobile, setIsMobile] = useState(() =>
    typeof window === "undefined"
      ? false
      : window.matchMedia("(max-width: 767px)").matches,
  );
  // P0-5/4.1 — coordenador de overlay mobile: UM por vez por construção.
  // null | 'filters' (menu lateral: Filtros+Frota+Incidentes+GIS) | 'device'
  // (bottom-sheet de detalhe: device OU cliente). Ignorado no desktop.
  const [mobileOverlay, setMobileOverlay] = useState(null);

  // P1-1 — modo de operação: 'operacional' (devices individuais, padrão) |
  // 'comercial' (agrupamento real por cliente).
  const [fleetMode, setFleetMode] = useState("operacional");
  // S3.11 — modo foco: mapa quase full-viewport (escapa topbar/sidebar/max-width).
  // Padrão = tela cheia NO DESKTOP (pedido do operador): a aba Mapa já abre
  // ocupando a viewport inteira. NO MOBILE inicia encaixado (!isMobile) — em
  // tela cheia o header não cabe e o botão de sair sairia da tela (trap). O
  // botão "✕ Sair da tela cheia" (header da MapView) volta ao layout encaixado;
  // o effect abaixo dá invalidateSize() no mount.
  const [focusMode, setFocusMode] = useState(() => !isMobile);
  // Leaflet precisa recalcular o tamanho quando o container muda (foco on/off).
  useEffect(() => {
    const t = setTimeout(() => {
      try { mapRef.current?.invalidateSize(); } catch { /* noop */ }
    }, 80);
    return () => clearTimeout(t);
  }, [focusMode]);
  // IXC imersivo — Esc sai da tela cheia (afordância de popup; garante saída
  // mesmo se os controles do mapa não renderizarem, ex.: Leaflet via CDN falhou).
  useEffect(() => {
    if (!focusMode) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setFocusMode(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focusMode]);
  const [focusCustomerKey, setFocusCustomerKey] = useState(null);
  const customerMarkersRef = useRef(new Map());
  const didFitCustomersRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [addModal, setAddModal] = useState(null);
  const [timelineDeviceId, setTimelineDeviceId] = useState(null);
  // Sprint 4.4 — DeviceConfigModal (botão ⚙ Configurar do popup)
  const [configDeviceId, setConfigDeviceId] = useState(null);
  const [selectionPreview, setSelectionPreview] = useState(null);

  const [autoRefreshError, setAutoRefreshError] = useState(false);
  const [mapStyle, setMapStyle] = useState(DEFAULT_STYLE);

  // Sprint 4.2C — GIS state
  const [gisPrefs, setGisPrefs] = useState(() => loadGisPrefs());
  const [regions, setRegions] = useState([]);
  const [nodes, setNodes] = useState([]);
  // Map<device_id, [DeviceNetworkLinkOut]> — populado on-demand
  const [deviceLinksMap, setDeviceLinksMap] = useState(() => new Map());

  // Sprint 4.2D — Clientes IXC (camada do mapa)
  const [customersIxc, setCustomersIxc] = useState([]);

  // Modais GIS
  const [regionModal, setRegionModal] = useState({ open: false, mode: "create", initial: null, drawnGeometry: null });
  const [nodeModal, setNodeModal] = useState({ open: false, mode: "create", initial: null, clickedLatLng: null });
  const [integrationsOpen, setIntegrationsOpen] = useState(false);

  // Flag: o próximo selection-finalize deve abrir RegionFormModal ao invés
  // de selecionar markers.
  const pendingRegionDrawRef = useRef(false);

  // Persistir GIS prefs
  useEffect(() => { saveGisPrefs(gisPrefs); }, [gisPrefs]);

  const refresh = useCallback(async (signal, { initial = false } = {}) => {
    try {
      if (initial) setLoading(true);
      setError(null);
      const [d, s, c, g, t, rg, nd, cmap, hl] = await Promise.all([
        getFleetMapDevices({ signal }),
        getFleetMapStats({ signal }),
        listCustomers({ signal }),
        listGroups({ signal }),
        listTags({ signal }),
        listFleetRegions({ signal }).catch(() => ({ regions: [] })),
        listNetworkNodes({ signal }).catch(() => ({ nodes: [] })),
        // Sprint 4.2D — só os que têm coords; graceful 404
        listCustomersForMap({ onlyWithCoords: true, signal })
          .catch(() => ({ customers: [] })),
        // M1.2 — Health Map: join client-side do health_score (endpoint
        // existente; SEM backend/API novo). Graceful: sem score → marker
        // cai para a cor por health_status (comportamento anterior).
        listHealth(undefined, { signal }).catch(() => ({ items: [] })),
      ]);
      // M1.2 — mescla health_score (0-100) em cada device pelo device_id.
      // MAP-HEALTH-1B — além do score, carrega reboot_count_24h e
      // watchdog_count_24h (janela REAL de 24h, já retornados por /fleet/health)
      // p/ o tooltip/painel exibirem "saúde recente". É só dado de exibição —
      // NÃO altera scoreClass nem a cor do marcador (essa segue de health_score).
      const healthById = new Map(
        (hl.items || []).map((h) => [h.device_id, h]),
      );
      // MAP-DATA-1B — ota_protected vem DIRETO do DTO do fleet-map/devices
      // (backend _is_ota_protected via OTA_PROTECTED_DEVICE_IDS). Não há mais
      // merge client-side do getFleetProduction: o DTO já é a fonte única.
      // MAP-UX-1A — o target_release_version vem no ENVELOPE (1 por frota);
      // denormaliza por device p/ o marcador/tooltip lerem só de `device`
      // (fw fora do alvo). null-safe: ausente → undefined, sem ring/linha.
      const targetVersion = d.target_release_version ?? null;
      const devicesWithScore = (d.devices || []).map((dev) => {
        const base = { ...dev };
        const h = healthById.get(dev.device_id);
        if (h) {
          base.health_score = h.health_score;
          base.reboot_count_24h = h.reboot_count_24h;
          base.watchdog_count_24h = h.watchdog_count_24h;
        }
        base.target_release_version = targetVersion;
        return base;
      });
      setDevices(devicesWithScore);
      setStats(s);
      setCustomers(c.customers || []);
      setGroups(g.groups || []);
      setTags(t.tags || []);
      setRegions(rg.regions || []);
      setNodes(nd.nodes || []);
      setCustomersIxc(cmap.customers || []);
      setAutoRefreshError(false);
    } catch (e) {
      if (e?.name === "AbortError") return;
      if (initial) setError(e?.message || "Erro ao carregar Fleet Map");
      else setAutoRefreshError(true);
      throw e;
    } finally {
      if (initial) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ctl = new AbortController();
    refresh(ctl.signal, { initial: true }).catch(() => {});
    return () => ctl.abort();
  }, [refresh]);

  // Inicializa Leaflet
  useEffect(() => {
    if (!L || !containerRef.current || mapRef.current) return;
    const m = L.map(containerRef.current, {
      center: INITIAL_CENTER, zoom: INITIAL_ZOOM,
      zoomControl: true, attributionControl: true,
      doubleClickZoom: true,
    });
    const initial = TILE_PROVIDERS[DEFAULT_STYLE];
    tileLayerRef.current = L.tileLayer(initial.url, {
      maxZoom: initial.maxZoom,
      attribution: initial.attribution,
      subdomains: initial.subdomains || "abc",
    }).addTo(m);
    mapRef.current = m;
    m.on("contextmenu", (e) => {
      setAddModal({ lat: e.latlng.lat, lng: e.latlng.lng });
    });
    return () => {
      m.remove();
      mapRef.current = null;
      tileLayerRef.current = null;
      clusterLayerRef.current = null;
      markersByIdRef.current.clear();
    };
  }, [L]);

  // Troca de estilo
  useEffect(() => {
    const map = mapRef.current;
    const tile = tileLayerRef.current;
    if (!map || !tile || !L) return;
    const p = TILE_PROVIDERS[mapStyle] || TILE_PROVIDERS[DEFAULT_STYLE];
    tile.setUrl(p.url);
    if (tile.options) {
      tile.options.attribution = p.attribution;
      tile.options.maxZoom = p.maxZoom;
      tile.options.subdomains = p.subdomains || "abc";
    }
  }, [L, mapStyle]);

  // Refs para useMapSelection
  const devicesRef = useRef(devices);
  useEffect(() => { devicesRef.current = devices; }, [devices]);

  const selection = useMapSelection({
    L, mapRef,
    onPreview: setSelectionPreview,
    getDevices: () => devicesRef.current,
  });

  useDeviceStateChanges(devices);

  // Listener selection-finalize: se foi disparado em modo "draw region",
  // abre modal com geometry em vez de selecionar markers.
  useEffect(() => {
    const onFinalize = (e) => {
      if (pendingRegionDrawRef.current) {
        pendingRegionDrawRef.current = false;
        const geom = selection.getLastDrawnGeoJson?.();
        setSelectionPreview(null);
        if (geom) {
          setRegionModal({ open: true, mode: "create", initial: null, drawnGeometry: geom });
        } else {
          setError("Não foi possível capturar a geometria desenhada. Tente novamente.");
        }
        return;
      }
      // Comportamento normal: selecionar markers
      const { additive } = e.detail || {};
      const inside = selection.getDevicesInsideLayer(devicesRef.current);
      const newIds = inside.map((d) => d.device_id);
      setSelectedIds((prev) => {
        const next = additive ? new Set(prev) : new Set();
        for (const id of newIds) next.add(id);
        return next;
      });
      setSelectionPreview(null);
    };
    document.addEventListener("ipwd:selection-finalize", onFinalize);
    return () => document.removeEventListener("ipwd:selection-finalize", onFinalize);
  }, [selection]);

  // Indexes auxiliares para busca GIS — Map<device_id, [regions/nodes]>
  // Pre-compute uma vez por mudança de devices/regions/nodes/links.
  const { regionsByDevice, nodesByDevice } = useMemo(() => {
    // regionsByDevice: usa ray-casting client-side simples (lat/lng) só
    // para enriquecer busca por q. Para precisão usa-se /devices endpoint.
    const rbd = new Map();
    for (const d of devices) {
      if (d.lat == null || d.lng == null) continue;
      for (const r of regions) {
        if (_pointInGeometry(d.lng, d.lat, r.geometry_geojson)) {
          if (!rbd.has(d.device_id)) rbd.set(d.device_id, []);
          rbd.get(d.device_id).push(r);
        }
      }
    }
    // nodesByDevice: usa deviceLinksMap.
    const nbd = new Map();
    const nodesById = new Map(nodes.map((n) => [n.id, n]));
    for (const [deviceId, links] of deviceLinksMap.entries()) {
      const ns = [];
      for (const l of links) {
        const n = nodesById.get(l.node_id);
        if (n) ns.push(n);
      }
      if (ns.length > 0) nbd.set(deviceId, ns);
    }
    return { regionsByDevice: rbd, nodesByDevice: nbd };
  }, [devices, regions, nodes, deviceLinksMap]);

  // Filtered devices
  const filteredDevices = useMemo(
    () => devices.filter((d) => {
      if (!matchesFilters(d, filters, selectedIds, regionsByDevice, nodesByDevice)) return false;
      const cat = deviceLayerCategory(d);
      if (!gisPrefs.markersFilter[cat]) return false;
      // MAP-UX-1D — filtro rápido (chip-bar), aditivo aos filtros acima.
      if (quickFilter && !QUICK_MATCHERS[quickFilter]?.(d)) return false;
      return true;
    }),
    [devices, filters, selectedIds, gisPrefs.markersFilter, regionsByDevice, nodesByDevice, quickFilter]
  );

  // MAP-UX-1D — counters ricos (fleet-wide, calculados no frontend a partir do
  // DTO). Cada chip mostra o N da sua categoria; `all` = total da frota.
  const quickCounts = useMemo(() => {
    const c = { all: devices.length };
    for (const k of Object.keys(QUICK_MATCHERS)) c[k] = 0;
    for (const d of devices) {
      for (const k of Object.keys(QUICK_MATCHERS)) {
        if (QUICK_MATCHERS[k](d)) c[k] += 1;
      }
    }
    return c;
  }, [devices]);

  const candidatesNoGeo = useMemo(
    () => devices.filter((d) => d.lat == null || d.lng == null),
    [devices]
  );

  // Map indices para topology layer
  const nodesById = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);
  const devicesById = useMemo(() => new Map(devices.map((d) => [d.device_id, d])), [devices]);

  // Heatmap
  useHeatmapLayer({
    L, mapRef,
    mode: gisPrefs.heatmapMode,
    devices,
    ready: heatReady,
  });

  // Sprint 4.2C — regiões / nodes / topologia
  useRegionsLayer({
    L, mapRef,
    regions,
    visible: gisPrefs.showRegions,
    onRegionClick: (r) => {
      // Foca + abre prompt simples; modal completo só via GisPanel "✎"
      handleFocusRegion(r);
    },
  });

  useNetworkNodesLayer({
    L, mapRef,
    nodes,
    visibility: gisPrefs.nodeVisibility,
    onNodeClick: (n) => {
      // Foca + foco visual; edição via GisPanel "✎"
      if (n.lat != null && n.lng != null && mapRef.current) {
        mapRef.current.setView([n.lat, n.lng], 17, { animate: true });
      }
    },
  });

  useTopologyLayer({
    L, mapRef,
    visible: gisPrefs.showTopology,
    deviceLinksMap,
    nodesById,
    devicesById,
  });

  // Sprint 4.2D — Clientes IXC layer
  useCustomersLayer({
    L, mapRef,
    customers: customersIxc,
    visible: gisPrefs.showCustomers,
    onCustomerClick: (c) => {
      if (c.lat != null && c.lng != null && mapRef.current) {
        mapRef.current.setView([c.lat, c.lng], 18, { animate: true });
      }
    },
  });

  // Foca uma região: ajusta bounds e abre detail "leve" (alert por enquanto)
  const handleFocusRegion = useCallback(async (r) => {
    if (!mapRef.current || !L || !r?.geometry_geojson) return;
    try {
      const layer = L.geoJSON(r.geometry_geojson);
      mapRef.current.fitBounds(layer.getBounds(), { padding: [40, 40] });
    } catch {
      /* ignore */
    }
  }, [L]);

  const handleDeleteRegion = useCallback(async (r) => {
    if (!hasCapability(CAP.FLEET_WRITE)) { setError("Seu usuário não tem permissão para esta ação."); return; }
    if (!window.confirm(`Excluir região "${r.name}"? Esta ação é irreversível.`)) return;
    try {
      await deleteFleetRegion(r.id);
      refresh(null, { initial: false });
    } catch (e) {
      setError(e?.message || `Erro ao excluir região ${r.name}`);
    }
  }, [refresh]);

  const handleDeleteNode = useCallback(async (n) => {
    if (!hasCapability(CAP.FLEET_WRITE)) { setError("Seu usuário não tem permissão para esta ação."); return; }
    if (!window.confirm(`Excluir ${n.type} "${n.name}"? Vínculos device→node serão removidos em cascade.`)) return;
    try {
      await deleteNetworkNode(n.id);
      refresh(null, { initial: false });
    } catch (e) {
      setError(e?.message || `Erro ao excluir ${n.name}`);
    }
  }, [refresh]);

  // Iniciar criação de região: ativa lasso e marca flag
  const handleCreateRegion = useCallback(() => {
    if (!selection || !selection.startMode) {
      setError("Seleção espacial indisponível. Recarregue a página.");
      return;
    }
    pendingRegionDrawRef.current = true;
    selection.startMode("lasso");
  }, [selection]);

  // Popup actions (inalterado)
  const handlePopupAction = useCallback(async (action, deviceId) => {
    try {
      switch (action) {
        case "relay_on": {
          // Relé ON — não derruba serviço → confirmação simples (sem senha).
          if (!hasCapability(CAP.DEVICES_COMMAND)) { setError("Seu usuário não tem permissão para esta ação."); return; }
          if (!window.confirm(`Confirma ${action} em ${deviceId}?`)) return;
          await enqueueSimpleCommand(deviceId, action);
          window.alert(`${action} enfileirado para ${deviceId}.`);
          break;
        }
        case "relay_off":
        case "reboot": {
          // S3.10A — ação crítica → confirmação com senha/ADMIN_TOKEN.
          if (!hasCapability(CAP.DEVICES_COMMAND)) { setError("Seu usuário não tem permissão para esta ação."); return; }
          const secret = await confirmCritical({
            actionLabel: action === "relay_off" ? "Desligar relé (relay_off)" : "Reiniciar ESP32 (reboot)",
            target: { kind: "single", deviceId },
            impact: action === "relay_off"
              ? "O equipamento conectado perde energia até religar o relé."
              : "O ESP32 reinicia e fica brevemente offline.",
          });
          if (secret == null) return;
          await enqueueSimpleCommand(deviceId, action, { confirm: secret });
          window.alert(`${action} enfileirado para ${deviceId}.`);
          break;
        }
        case "relay_reboot": {
          // S3.10A — pulso de relé (corta energia ~10s) → ação crítica.
          if (!hasCapability(CAP.DEVICES_COMMAND)) { setError("Seu usuário não tem permissão para esta ação."); return; }
          const secret = await confirmCritical({
            actionLabel: "Reiniciar relé (relay_reboot, 10s)",
            target: { kind: "single", deviceId },
            impact: "A energia do equipamento é cortada por ~10s e religada.",
          });
          if (secret == null) return;
          await enqueueRelayReboot(deviceId, 10, { confirm: secret });
          window.alert(`relay_reboot enfileirado para ${deviceId}.`);
          break;
        }
        case "ota": {
          if (onNavigate) onNavigate("firmware");
          break;
        }
        case "move": {
          // MAP-MANAGE-1E-B — gate de capability antes de entrar no modo mover.
          if (!hasCapability(CAP.FLEET_WRITE)) {
            setError("Seu usuário não tem permissão para mover devices (fleet:write).");
            break;
          }
          if (dragModeRef.current.has(deviceId)) {
            dragModeRef.current.delete(deviceId);
          } else {
            dragModeRef.current.add(deviceId);
            window.alert(`Modo MOVER ativado para ${deviceId}. Arraste o marker.`);
          }
          setFocusDeviceId(deviceId);
          setDevices((prev) => prev.slice());
          break;
        }
        case "select": {
          setSelectedIds((prev) => { const n = new Set(prev); n.add(deviceId); return n; });
          break;
        }
        case "history": {
          setTimelineDeviceId(deviceId);
          // Carrega links se ainda não tem (alimenta nodesByDevice)
          if (!deviceLinksMap.has(deviceId)) {
            listDeviceLinks(deviceId).then((res) => {
              setDeviceLinksMap((prev) => {
                const next = new Map(prev);
                next.set(deviceId, res.links || []);
                return next;
              });
            }).catch(() => {});
          }
          break;
        }
        case "open": {
          if (onNavigate) onNavigate("detail", deviceId);
          break;
        }
        case "focus": {
          // Etapa 3.1 — foca o device no painel direito (somente leitura).
          setFocusDeviceId(deviceId);
          break;
        }
        case "config": {
          // Sprint 4.4 — abre DeviceConfigModal. S3.6 — exige devices:config.
          if (!hasCapability(CAP.DEVICES_CONFIG)) {
            setError("Seu usuário não tem permissão para esta ação.");
            break;
          }
          setConfigDeviceId(deviceId);
          break;
        }
        default:
          console.warn("Ação desconhecida:", action);
      }
    } catch (e) {
      setError(e?.message || `Erro em ${action}`);
    }
  }, [onNavigate, deviceLinksMap]);

  const handleDragEnd = useCallback(async ({ device, lat, lng }) => {
    // MAP-MANAGE-1E-B — frontend é atrito/UX; backend é a fonte da verdade.
    // revert: re-roda o marker effect, que reseta o pin p/ device.lat/lng (coord
    // salva), descartando o arrasto não-persistido.
    const revert = () => setDevices((prev) => prev.slice());
    if (!hasCapability(CAP.FLEET_WRITE)) {
      setError("Seu usuário não tem permissão para mover devices (fleet:write).");
      revert();
      return;
    }
    const oldPos = (device.lat != null && device.lng != null)
      ? `${device.lat.toFixed(5)}, ${device.lng.toFixed(5)}` : "sem posição";
    const newPos = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
    let confirm;
    if (getProtectionState(device).protected) {
      // Device protegido (produção): alteração de localização exige confirmação forte.
      const secret = await confirmCritical({
        actionLabel: "Mover device PROTEGIDO (produção) no mapa",
        target: { kind: "device", id: device.device_id },
        impact: "Device protegido: alterar a localização exige confirmação forte. "
          + `De ${oldPos} → ${newPos}. Isso altera apenas o pin no NOC.`,
      });
      if (secret == null) { revert(); return; }  // cancelou → reverte o pin
      confirm = secret;
    } else if (!window.confirm(
      `Mover ${device.device_id} de ${oldPos} para ${newPos}?\n`
      + "Isso altera apenas o pin no mapa do NOC.",
    )) {
      revert();
      return;
    }
    try {
      await setDeviceGeo(
        device.device_id, { lat, lng, name: device.location_name }, { confirm },
      );
      dragModeRef.current.delete(device.device_id);
      refresh(null, { initial: false });
    } catch (e) {
      const why = e?.status === 403
        ? `403 — ${e?.message || "permissão ou confirmação forte necessária"}`
        : (e?.message || "erro ao salvar");
      setError(`Localização não salva (${device.device_id}): ${why}`);
      refresh(null, { initial: false });  // re-fetch reverte o pin p/ a coord salva
    }
  }, [refresh, confirmCritical]);

  useEffect(() => {
    const handler = (e) => {
      const { action, deviceId } = e.detail || {};
      if (action && deviceId) handlePopupAction(action, deviceId);
    };
    document.addEventListener("ipwd:popup-action", handler);
    return () => document.removeEventListener("ipwd:popup-action", handler);
  }, [handlePopupAction]);

  useEffect(() => {
    const delegate = (e) => {
      const btn = e.target.closest("button[data-action][data-device]");
      if (!btn) return;
      const action = btn.getAttribute("data-action");
      const deviceId = btn.getAttribute("data-device");
      e.preventDefault();
      e.stopPropagation();
      document.dispatchEvent(new CustomEvent("ipwd:popup-action", {
        detail: { action, deviceId },
      }));
    };
    document.addEventListener("click", delegate, true);
    return () => document.removeEventListener("click", delegate, true);
  }, []);

  const toggleSelect = useCallback((deviceId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(deviceId)) next.delete(deviceId);
      else next.add(deviceId);
      return next;
    });
  }, []);

  const handleMarkerClick = useCallback(({ device, ctrlKey, metaKey }) => {
    if (ctrlKey || metaKey) {
      toggleSelect(device.device_id);
    } else {
      setFocusDeviceId(device.device_id);
      // P0-5 — no mobile, tocar no marker abre o bottom-sheet (e fecha os
      // demais overlays por construção). No desktop, 'device' é ignorado.
      setMobileOverlay("device");
    }
  }, [toggleSelect]);

  // P1-1 — clique no marker de cliente (modo comercial) → foca o cliente.
  const handleCustomerClick = useCallback((key) => {
    setFocusCustomerKey(key);
    setMobileOverlay("device"); // reusa o slot de "detalhe" no mobile
  }, []);

  // P1-1 — troca de modo limpa o foco/overlay p/ não deixar painel órfão.
  const switchFleetMode = useCallback((mode) => {
    setFleetMode(mode);
    setFocusDeviceId(null);
    setFocusCustomerKey(null);
    setMobileOverlay(null);
    didFitCustomersRef.current = false;
  }, []);

  // Markers render — cluster condicional.
  // P0-3 — o cluster por PROXIMIDADE é puramente um recurso técnico
  // anti-poluição: ativa só acima de CLUSTER_THRESHOLD devices. NÃO é
  // mais exposto como "modo" na UI (o seletor Normal/Concentrado foi
  // removido). O agrupamento REAL por cliente (Modo Comercial) será um
  // recurso separado e futuro — o DTO já carrega customer_id/code/name.
  const useCluster = clusterReady && filteredDevices.length > CLUSTER_THRESHOLD;

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !L) return;
    const byId = markersByIdRef.current;

    // P1-1 — fora do modo Operacional, remove TODOS os markers individuais
    // (e o cluster). Ao voltar, byId fica vazio → são recriados abaixo.
    if (fleetMode !== "operacional") {
      if (clusterLayerRef.current) {
        for (const [, m] of byId) clusterLayerRef.current.removeLayer(m);
        map.removeLayer(clusterLayerRef.current);
        clusterLayerRef.current = null;
      }
      for (const [, m] of byId) { if (map.hasLayer(m)) map.removeLayer(m); }
      byId.clear();
      return;
    }

    if (useCluster && !clusterLayerRef.current && L.markerClusterGroup) {
      clusterLayerRef.current = L.markerClusterGroup({
        showCoverageOnHover: false,
        spiderfyOnMaxZoom: true,
        maxClusterRadius: 50,
        disableClusteringAtZoom: 17,
      });
      for (const [, m] of byId.entries()) {
        map.removeLayer(m);
        clusterLayerRef.current.addLayer(m);
      }
      map.addLayer(clusterLayerRef.current);
    } else if (!useCluster && clusterLayerRef.current) {
      for (const [, m] of byId.entries()) {
        clusterLayerRef.current.removeLayer(m);
        map.addLayer(m);
      }
      map.removeLayer(clusterLayerRef.current);
      clusterLayerRef.current = null;
    }

    const visible = filteredDevices.filter((d) => d.lat != null && d.lng != null);
    const visibleIds = new Set();
    const bounds = [];

    for (const d of visible) {
      visibleIds.add(d.device_id);
      bounds.push([d.lat, d.lng]);
      const selected = selectedIds.has(d.device_id);
      const focused = d.device_id === focusDeviceId;   // P1-4
      const draggable = dragModeRef.current.has(d.device_id);
      let marker = byId.get(d.device_id);
      if (marker) {
        updateDeviceMarker(L, marker, d, { selected, draggable, focused });
        if (clusterLayerRef.current) {
          if (!clusterLayerRef.current.hasLayer(marker)) {
            if (map.hasLayer(marker)) map.removeLayer(marker);
            clusterLayerRef.current.addLayer(marker);
          }
        } else {
          if (!map.hasLayer(marker)) marker.addTo(map);
        }
      } else {
        marker = createDeviceMarker(L, d, {
          selected, draggable, focused,
          onClick: handleMarkerClick,
          onDragEnd: handleDragEnd,
        });
        if (marker) {
          if (clusterLayerRef.current) {
            clusterLayerRef.current.addLayer(marker);
          } else {
            marker.addTo(map);
          }
          byId.set(d.device_id, marker);
        }
      }
    }

    for (const [did, m] of byId.entries()) {
      if (!visibleIds.has(did)) {
        if (clusterLayerRef.current && clusterLayerRef.current.hasLayer(m)) {
          clusterLayerRef.current.removeLayer(m);
        } else if (map.hasLayer(m)) {
          map.removeLayer(m);
        }
        byId.delete(did);
      }
    }

    if (!didFitBoundsRef.current && visible.length > 0 && !focusDeviceId) {
      if (visible.length > 1) {
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
      } else {
        map.setView(bounds[0], 16);
      }
      didFitBoundsRef.current = true;
    }
  }, [L, filteredDevices, selectedIds, focusDeviceId, useCluster, fleetMode,
      handleMarkerClick, handleDragEnd]);

  // P1-1 — agrupamento por cliente (memo) para markers + painel comercial.
  const customerGroups = useMemo(
    () => (fleetMode === "comercial" ? groupByCustomer(filteredDevices) : []),
    [fleetMode, filteredDevices],
  );

  // P1-1 — render dos markers AGREGADOS de cliente (só no modo comercial).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !L) return;
    const byKey = customerMarkersRef.current;

    if (fleetMode !== "comercial") {
      for (const [, m] of byKey) { if (map.hasLayer(m)) map.removeLayer(m); }
      byKey.clear();
      return;
    }

    const placeable = customerGroups.filter((g) => g.lat != null && g.lng != null);
    const seen = new Set();
    const bounds = [];
    for (const g of placeable) {
      seen.add(g.key);
      bounds.push([g.lat, g.lng]);
      // Recria o marker p/ refletir stats atualizadas (N de clientes é baixo).
      const old = byKey.get(g.key);
      if (old && map.hasLayer(old)) map.removeLayer(old);
      const m = createCustomerMarker(L, g, {
        onClick: handleCustomerClick,
        focused: g.key === focusCustomerKey,   // P1-4 — destaque do cliente focado
      });
      if (m) { m.addTo(map); byKey.set(g.key, m); }
    }
    for (const [k, m] of byKey) {
      if (!seen.has(k)) { if (map.hasLayer(m)) map.removeLayer(m); byKey.delete(k); }
    }

    if (!didFitCustomersRef.current && bounds.length > 0) {
      if (bounds.length > 1) map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
      else map.setView(bounds[0], 14);
      didFitCustomersRef.current = true;
    }
  }, [L, fleetMode, customerGroups, focusCustomerKey, handleCustomerClick]);

  // ----- HOTFIX UX-1: focus/popup só por ação manual do usuário -----
  // O effect dispara APENAS quando focusDeviceId muda (clique no marker /
  // clique na lista lateral). NÃO depende de `devices`, então o auto-refresh
  // (que recria o array de devices) NÃO reabre popup nem re-centraliza.
  //
  // Usamos uma ref para acessar `devices` mais recente sem participar das
  // deps do effect.
  const devicesForFocusRef = useRef(devices);
  useEffect(() => { devicesForFocusRef.current = devices; }, [devices]);

  useEffect(() => {
    if (!mapRef.current || !focusDeviceId) return;
    const d = devicesForFocusRef.current.find((x) => x.device_id === focusDeviceId);
    if (!d || d.lat == null || d.lng == null) return;
    // P1-4 — flyTo suave (não move se coords ausentes — já checado acima).
    mapRef.current.flyTo([d.lat, d.lng], 17, { duration: 0.6 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusDeviceId]);

  // P1-4 — flyTo ao focar um CLIENTE (modo comercial).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || fleetMode !== "comercial" || !focusCustomerKey) return;
    const g = customerGroups.find((x) => x.key === focusCustomerKey);
    if (g && g.lat != null && g.lng != null) {
      map.flyTo([g.lat, g.lng], 15, { duration: 0.6 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusCustomerKey]);

  // Auto-refresh com pause
  const hasOpenModal = addModal !== null
    || timelineDeviceId !== null
    || configDeviceId !== null            // Sprint 4.4
    || regionModal.open
    || nodeModal.open
    || integrationsOpen;
  const hasDragActive = dragModeRef.current.size > 0;
  const isSelecting = selection.mode !== "idle" || selection.drawing;
  const refreshPaused = hasOpenModal || hasDragActive || isSelecting;

  const { lastUpdate } = useAutoRefresh({
    intervalMs: REFRESH_MS,
    paused: refreshPaused,
    callback: () => refresh(null, { initial: false }),
  });

  let pauseReason = null;
  if (hasOpenModal) pauseReason = "Modal aberto";
  else if (hasDragActive) pauseReason = "Marker em drag";
  else if (isSelecting) {
    pauseReason = pendingRegionDrawRef.current
      ? "Desenhando região" : "Selecionando área";
  }

  // P0-1/P0-4 — device em foco alimenta o Painel direito (desktop) e o
  // bottom-sheet (mobile).
  const focusedDevice = focusDeviceId
    ? devices.find((d) => d.device_id === focusDeviceId) || null
    : null;

  // P1-1 — cliente em foco (modo comercial) alimenta o painel/sheet.
  const focusedCustomer =
    fleetMode === "comercial" && focusCustomerKey
      ? customerGroups.find((g) => g.key === focusCustomerKey) || null
      : null;

  // Ações de navegação do painel de cliente (sem escrita).
  const filterCustomer = (g) => {
    if (!g?.id) return;
    setFilters((f) => ({ ...f, customer_id: g.id }));
  };
  const openCustomerInOperacional = (g) => {
    if (!g?.id) return;
    if (g.lat != null && g.lng != null) {
      mapRef.current?.flyTo([g.lat, g.lng], 15, { duration: 0.6 });
    }
    setFilters((f) => ({ ...f, customer_id: g.id }));
    switchFleetMode("operacional");
  };
  const focusDeviceFromCustomer = (id) => {
    switchFleetMode("operacional");
    setFocusDeviceId(id);
    setMobileOverlay("device");
  };
  const focusCustomerOnMap = (g) => {
    if (g?.lat != null && g?.lng != null) {
      mapRef.current?.flyTo([g.lat, g.lng], 15, { duration: 0.6 });
    }
  };

  // P0-5 — visibilidade dos overlays:
  //  · desktop → comportamento atual (filtersOpen; Frota/GIS internos).
  //  · mobile  → tudo deriva de `mobileOverlay` (um por vez).
  const filtersVisible = isMobile ? mobileOverlay === "filters" : filtersOpen;
  const openFilters = () =>
    isMobile ? setMobileOverlay("filters") : setFiltersOpen(true);
  const closeFilters = () =>
    isMobile ? setMobileOverlay(null) : setFiltersOpen(false);
  // Sheet mobile visível quando o overlay ativo é 'device' e há um foco
  // (device no operacional ou cliente no comercial).
  const sheetVisible =
    isMobile && mobileOverlay === "device" && (!!focusedDevice || !!focusedCustomer);

  // P1-1 — seletor de modo (Operacional | Comercial). Fora do foco vive no
  // header; no modo foco/tela-cheia (IXC imersivo) flutua sobre o mapa (topleft).
  const modeToggleEl = (
    <div className="ipwd-mode-toggle" role="group" aria-label="Modo de operação">
      <button
        type="button"
        className={fleetMode === "operacional" ? "is-active" : ""}
        onClick={() => switchFleetMode("operacional")}
      >Operacional</button>
      <button
        type="button"
        className={fleetMode === "comercial" ? "is-active" : ""}
        onClick={() => switchFleetMode("comercial")}
        title="Agrupar por cliente"
      >Comercial</button>
    </div>
  );

  return (
    <div className={focusMode
      ? "ipwd-fleet-shell ipwd-fleet-focus bg-noc-bg space-y-2"
      : "ipwd-fleet-shell space-y-3"}>
      {/* Header do shell — ESCONDIDO no modo foco/tela-cheia (IXC imersivo): lá
          os controles (modo + ✕ fechar) flutuam sobre o mapa, mapa edge-to-edge.
          Mostrado fora do foco (mobile + desktop encaixado), com o botão de
          ENTRAR em tela cheia. */}
      {!focusMode && (
        <header className="ipwd-fleet-header">
          <div className="ipwd-fleet-header-title">
            🛰 Fleet Explorer <span>· Centro Operacional</span>
          </div>
          <div className="ipwd-fleet-header-slot">
            {modeToggleEl}
            {/* S3.11 — entrar em tela cheia / modo foco do mapa */}
            <button
              type="button"
              onClick={() => setFocusMode(true)}
              className="text-xs px-2.5 py-1 rounded border border-noc-border text-noc-muted hover:text-noc-text hover:border-noc-accent/50"
              title="Tela cheia do mapa (mais área útil)"
            >
              ⛶ Tela cheia
            </button>
          </div>
        </header>
      )}

      {/* IXC imersivo — SAIR da tela cheia (afordância de "fechar" do popup).
          Renderizado no nível do shell em foco, FORA do gate {leafletReady} →
          a saída existe SEMPRE, mesmo se o Leaflet (CDN) falhar ou estiver
          carregando. Esc também sai (effect com [focusMode]). */}
      {focusMode && (
        <button
          type="button"
          className="ipwd-map-close ipwd-focus-exit"
          onClick={() => setFocusMode(false)}
          title="Sair da tela cheia (Esc)"
          aria-label="Sair da tela cheia"
        >✕</button>
      )}

      {leafletError && (
        <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2">
          Falha ao carregar Leaflet via CDN: {String(leafletError.message)}
        </div>
      )}
      {error && (
        <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2 flex items-start gap-2">
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError(null)} className="text-noc-err hover:text-noc-err">×</button>
        </div>
      )}

      <SelectionToolbar
        mode={selection.mode}
        startMode={selection.startMode}
        cancelMode={selection.cancelMode}
        previewCount={selectionPreview}
      />

      <div className="flex gap-3 flex-col md:flex-row md:items-start">
        {/* M1.1C — backdrop do drawer (só visível no mobile via CSS) */}
        {filtersVisible && (
          <div
            className="ipwd-fleet-backdrop"
            onClick={closeFilters}
          />
        )}
        {filtersVisible && (
          <aside className="ipwd-fleet-sidebar ipwd-noc-rail w-full md:w-[264px] flex-shrink-0 bg-noc-panel border border-noc-border rounded p-3 space-y-3 text-xs md:overflow-y-auto">
            {/* 1. Filtros */}
            <FleetFilters
              bare
              devices={filteredDevices}
              stats={stats}
              filters={filters}
              setFilters={setFilters}
              customers={customers}
              groups={groups}
              allTags={tags}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onSelectDevice={(id) => {
                setFocusDeviceId(id);
                setMobileOverlay("device");
              }}
              onResetFilters={() => setFilters(EMPTY_FILTERS)}
              onCollapse={closeFilters}
            />

            {/* 2. Frota */}
            <div className="ipwd-noc-rail-sec">
              <AlarmCenter
                devices={devices}
                filters={filters}
                setFilters={setFilters}
              />
            </div>

            {/* 3. Incidentes */}
            <div className="ipwd-noc-rail-sec">
              <IncidentsList
                devices={devices}
                onSelect={(id) => {
                  // P1-2 — clicar num incidente sempre leva ao Operacional e
                  // foca o device (caminho seguro mesmo vindo do Comercial).
                  switchFleetMode("operacional");
                  setFocusDeviceId(id);
                  setMobileOverlay("device");
                }}
              />
            </div>

            {/* 4. GIS / Camadas */}
            <div className="ipwd-noc-rail-sec">
              <GisPanel
                prefs={gisPrefs}
                setPrefs={setGisPrefs}
                regions={regions}
                nodes={nodes}
                customersCount={customersIxc.length}
                onFocusRegion={handleFocusRegion}
                onEditRegion={(r) => setRegionModal({ open: true, mode: "edit", initial: r, drawnGeometry: null })}
                onDeleteRegion={handleDeleteRegion}
                onCreateRegion={handleCreateRegion}
                onFocusNode={(n) => { if (n.lat != null && n.lng != null) mapRef.current?.setView([n.lat, n.lng], 17, { animate: true }); }}
                onEditNode={(n) => setNodeModal({ open: true, mode: "edit", initial: n, clickedLatLng: null })}
                onDeleteNode={handleDeleteNode}
                onCreateNode={() => setNodeModal({ open: true, mode: "create", initial: null, clickedLatLng: null })}
                onOpenIntegrations={() => setIntegrationsOpen(true)}
              />
            </div>
          </aside>
        )}

        <div className="flex-1 space-y-2">
          {!leafletReady && (
            <div className="bg-noc-panel border border-noc-border rounded p-6 text-center text-xs text-noc-muted">
              Carregando Leaflet via CDN…
            </div>
          )}
          {/* MAP-UX-1D — chip-bar de filtros rápidos + counters (só Operacional;
              no Comercial os markers individuais não existem). Fica FORA do
              .ipwd-map-wrap p/ não colidir com os overlays absolutos do mapa
              (Mapa/Auto-refresh, top:12px ancorados no wrap). */}
          {fleetMode === "operacional" && (
            <MapQuickFilters
              counts={quickCounts}
              active={quickFilter}
              onChange={setQuickFilter}
            />
          )}
          <div className="ipwd-map-wrap">
            {fleetMode === "operacional" && quickFilter && filteredDevices.length === 0 && (
              <div className="ipwd-qf-empty">
                Nenhum device com este filtro.{" "}
                <button type="button" className="ipwd-qf-empty-clear"
                        onClick={() => setQuickFilter(null)}>
                  Mostrar todos
                </button>
              </div>
            )}
            <div
              ref={containerRef}
              className="ipwd-map-shell rounded border border-noc-border overflow-hidden"
              style={{
                backgroundColor: "#1f2937",
                display: leafletReady ? "block" : "none",
                cursor: selection.mode !== "idle" ? "crosshair" : "grab",
              }}
            />
            {leafletReady && (
              <>
                <div className="ipwd-map-topleft">
                  {/* IXC imersivo: no modo foco o seletor de modo flutua aqui
                      (header escondido). Fora do foco vive no header. */}
                  {focusMode && modeToggleEl}
                  {!filtersVisible && (
                    <button
                      type="button"
                      onClick={openFilters}
                      title="Mostrar filtros"
                      className="bg-noc-panel/90 border border-noc-border rounded px-2.5 py-1 text-xs font-medium text-noc-text hover:bg-noc-panel shadow"
                    >
                      ☰ Filtros
                    </button>
                  )}
                  <MapStyleSwitcher
                    currentStyle={mapStyle}
                    onChange={setMapStyle}
                  />
                  {/* P0-3 — seletor "Normal/Concentrado" removido: o modo
                      concentrado era apenas cluster por proximidade, não
                      agrupamento real por cliente. O cluster segue ativo
                      internamente por densidade (anti-poluição). */}
                </div>

                <div className="ipwd-map-topright">
                  <div
                    className={`ipwd-autorefresh-pill ${refreshPaused ? "is-paused" : ""} ${autoRefreshError ? "is-error" : ""}`}
                    title={refreshPaused ? `Pausado: ${pauseReason}` : "Auto-refresh ativo"}
                  >
                    <span className="ipwd-autorefresh-dot" />
                    <span className="ipwd-autorefresh-label">
                      {refreshPaused ? "PAUSADO" : autoRefreshError ? "FALHA" : "AUTO REFRESH · 20s"}
                    </span>
                    <span className="ipwd-autorefresh-time">{fmtTime(lastUpdate)}</span>
                  </div>
                  {/* Etapa 4.1 — Frota e GIS saíram do mapa para o menu
                      lateral esquerdo. O topright fica só com o auto-refresh
                      discreto (mapa limpo, estilo NOC). */}
                </div>

              </>
            )}
          </div>
          {/* P0-3 — pill "Cluster ativo" removida (debug remanescente);
              a dica de uso permanece (P2). */}
          <div className="text-[10px] text-noc-muted/70 flex items-center gap-2">
            💡 Clique direito no mapa para adicionar device · Ctrl+click no marker para selecionar
            <span className="flex-1" />
            <button
              type="button"
              onClick={() => refresh(null, { initial: false })}
              disabled={loading}
              className="text-xs px-2 py-0.5 rounded border border-noc-ok/30 text-noc-ok hover:border-noc-ok/30 disabled:opacity-50"
            >
              {loading ? t("common.busy") : `🔄 ${t("common.refresh")}`}
            </button>
          </div>
        </div>

        {/* P0-4 — Painel direito operacional (zona 4), SOMENTE LEITURA.
            Desktop apenas (mobile usa o bottom-sheet, P0-5). */}
        {fleetMode === "operacional" && focusedDevice && !isMobile && (
          <DeviceDetailPanel
            device={focusedDevice}
            onClose={() => setFocusDeviceId(null)}
            onOpenDevice={(id, tab) => onNavigate?.("detail", id, tab)}
            onCommand={handlePopupAction}
          />
        )}

        {/* P1-1 — Painel direito de CLIENTE (modo comercial, desktop) */}
        {fleetMode === "comercial" && focusedCustomer && !isMobile && (
          <CustomerDetailPanel
            variant="panel"
            group={focusedCustomer}
            onClose={() => setFocusCustomerKey(null)}
            onFilterCustomer={filterCustomer}
            onOpenOperacional={openCustomerInOperacional}
            onFocusDevice={focusDeviceFromCustomer}
            onFocusOnMap={focusCustomerOnMap}
          />
        )}
      </div>

      <BulkActionBar
        selectedIds={selectedIds}
        onClear={() => setSelectedIds(new Set())}
        customers={customers}
        groups={groups}
        tags={tags}
        devices={devices}
        onAfterAction={() => refresh(null, { initial: false })}
      />

      {/* P0-5 — Bottom-sheet de device (mobile). Resumo somente leitura.
          Fechar NÃO mexe no mapa (apenas oculta o overlay). */}
      {sheetVisible && fleetMode === "operacional" && focusedDevice && (
        <MobileDeviceSheet
          device={focusedDevice}
          onClose={() => setMobileOverlay(null)}
          onOpenDevice={(id, tab) => onNavigate?.("detail", id, tab)}
          onCommand={handlePopupAction}
        />
      )}

      {/* P1-1 — bottom-sheet de CLIENTE (modo comercial, mobile) */}
      {sheetVisible && fleetMode === "comercial" && focusedCustomer && (
        <CustomerDetailPanel
          variant="sheet"
          group={focusedCustomer}
          onClose={() => setMobileOverlay(null)}
          onFilterCustomer={filterCustomer}
          onOpenOperacional={openCustomerInOperacional}
          onFocusDevice={focusDeviceFromCustomer}
          onFocusOnMap={(g) => { focusCustomerOnMap(g); setMobileOverlay(null); }}
        />
      )}

      <AddDeviceAtMapModal
        open={!!addModal}
        lat={addModal?.lat}
        lng={addModal?.lng}
        candidates={candidatesNoGeo}
        onClose={() => setAddModal(null)}
        onSaved={() => { setAddModal(null); refresh(null, { initial: false }); }}
      />

      <DeviceTimelineModal
        open={timelineDeviceId !== null}
        deviceId={timelineDeviceId}
        onClose={() => setTimelineDeviceId(null)}
      />

      {/* Sprint 4.4 — Remote Config Center. S3.6 — só monta com devices:config. */}
      {hasCapability(CAP.DEVICES_CONFIG) && (
        <DeviceConfigModal
          open={configDeviceId !== null}
          deviceId={configDeviceId}
          onClose={() => setConfigDeviceId(null)}
        />
      )}

      {/* Sprint 4.2C — modais GIS */}
      <RegionFormModal
        open={regionModal.open}
        mode={regionModal.mode}
        initial={regionModal.initial}
        drawnGeometry={regionModal.drawnGeometry}
        onClose={() => setRegionModal({ open: false, mode: "create", initial: null, drawnGeometry: null })}
        onSaved={() => {
          setRegionModal({ open: false, mode: "create", initial: null, drawnGeometry: null });
          refresh(null, { initial: false });
        }}
      />
      <NetworkNodeFormModal
        open={nodeModal.open}
        mode={nodeModal.mode}
        initial={nodeModal.initial}
        clickedLatLng={nodeModal.clickedLatLng}
        onClose={() => setNodeModal({ open: false, mode: "create", initial: null, clickedLatLng: null })}
        onSaved={() => {
          setNodeModal({ open: false, mode: "create", initial: null, clickedLatLng: null });
          refresh(null, { initial: false });
        }}
      />
      <IxcConfigModal
        open={integrationsOpen}
        onClose={() => setIntegrationsOpen(false)}
        onAfterSync={() => refresh(null, { initial: false })}
      />
    </div>
  );
}

// ---------- ray-casting client-side ----------
function _pointInRing(lng, lat, ring) {
  let inside = false;
  const n = ring.length;
  if (n < 3) return false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    if ((yi > lat) !== (yj > lat)) {
      const x = (xj - xi) * (lat - yi) / ((yj - yi) || 1e-30) + xi;
      if (lng < x) inside = !inside;
    }
  }
  return inside;
}
function _pointInPolygon(lng, lat, poly) {
  if (!poly || !poly.length) return false;
  if (!_pointInRing(lng, lat, poly[0])) return false;
  for (let i = 1; i < poly.length; i++) {
    if (_pointInRing(lng, lat, poly[i])) return false;
  }
  return true;
}
function _pointInGeometry(lng, lat, g) {
  if (!g || !g.type || !g.coordinates) return false;
  if (g.type === "Polygon") return _pointInPolygon(lng, lat, g.coordinates);
  if (g.type === "MultiPolygon") {
    for (const p of g.coordinates) if (_pointInPolygon(lng, lat, p)) return true;
  }
  return false;
}
